'use client'

import {
    pendingAgentAttachmentStore,
    type IPendingAgentAttachmentPayload,
} from '@/features/agents/agent-pending-attachment-store'
import { globalTaskComposerStore } from '@/features/agents/stores/agent-global-task-composer-store'

export const GLOBAL_TASK_COMPOSER_AGENT_UUID = '__global_task_composer__' as const

/** Dispatch options shared by attachment and prompt helpers. */
export interface ITaskComposerDispatchOptions {
    /** Route to the global Cmd+K composer store key without opening the menu. */
    target?: 'local' | 'global'
    /** Write to the global composer key and open Cmd+K (visible or silent via `autoSubmit`). */
    openNewTask?: boolean
    /** With `openNewTask`, submit immediately without showing the composer UI. */
    autoSubmit?: boolean
}

let openFlushScheduled = false
let pendingOpenMode: 'visible' | 'silent' | null = null

function scheduleGlobalTaskComposerOpen(autoSubmit: boolean) {
    if (autoSubmit) {
        pendingOpenMode = 'silent'
    } else if (pendingOpenMode !== 'silent') {
        pendingOpenMode = 'visible'
    }

    if (openFlushScheduled) return
    openFlushScheduled = true
    queueMicrotask(() => {
        openFlushScheduled = false
        const mode = pendingOpenMode
        pendingOpenMode = null
        if (mode === 'silent') {
            globalTaskComposerStore.getState().openSilent()
        } else if (mode === 'visible') {
            globalTaskComposerStore.getState().open()
        }
    })
}

function resolveComposerAgentUuid(
    localAgentUuid: string | null,
    options?: ITaskComposerDispatchOptions
): { agentUuid: string; shouldOpenComposer: boolean } {
    if (options?.openNewTask || options?.target === 'global') {
        return { agentUuid: GLOBAL_TASK_COMPOSER_AGENT_UUID, shouldOpenComposer: !!options?.openNewTask }
    }
    if (localAgentUuid) {
        return { agentUuid: localAgentUuid, shouldOpenComposer: false }
    }
    // No local chat — auto-route to Cmd+K
    return { agentUuid: GLOBAL_TASK_COMPOSER_AGENT_UUID, shouldOpenComposer: true }
}

export function dispatchAttachmentToActiveTaskTarget(
    attachment: IPendingAgentAttachmentPayload,
    localAgentUuid: string | null,
    options?: ITaskComposerDispatchOptions
) {
    const { agentUuid, shouldOpenComposer } = resolveComposerAgentUuid(localAgentUuid, options)
    if (shouldOpenComposer && options?.autoSubmit) {
        // Keep the headless auto-submit payload separate from visible composer drafts.
        globalTaskComposerStore.getState().queueSilentAttachment(attachment)
    } else {
        pendingAgentAttachmentStore.getState().addPendingAttachment(attachment, agentUuid)
    }
    if (shouldOpenComposer) {
        scheduleGlobalTaskComposerOpen(!!options?.autoSubmit)
    }
}

export function dispatchPromptToActiveTaskTarget(
    prompt: string,
    localAgentUuid: string | null,
    options?: ITaskComposerDispatchOptions
) {
    const { agentUuid, shouldOpenComposer } = resolveComposerAgentUuid(localAgentUuid, options)
    if (shouldOpenComposer && options?.autoSubmit) {
        // Keep the headless auto-submit payload separate from visible composer drafts.
        globalTaskComposerStore.getState().queueSilentPrompt(prompt)
    } else {
        pendingAgentAttachmentStore.getState().setPendingPrompt(prompt, agentUuid)
    }
    if (shouldOpenComposer) {
        scheduleGlobalTaskComposerOpen(!!options?.autoSubmit)
    }
}

export function dispatchNewTaskIntent({
    attachment,
    attachments,
    prompt,
    autoSubmit = false,
    skillMentions,
    channel,
    homeBriefingRecommendationId,
    startingMode,
    promptCategoryId,
    stayOnPage,
}: {
    attachment?: IPendingAgentAttachmentPayload
    /** Multiple attachments for the same new task (e.g. bulk-launching several creatives). Combined
     *  with `attachment` if both are given; the composer open is microtask-debounced so a batch still
     *  opens a single task. */
    attachments?: IPendingAgentAttachmentPayload[]
    prompt?: string
    autoSubmit?: boolean
    skillMentions?: Array<{ label: string }>
    /** Marketing-channel slug to preselect in the composer (e.g. `meta-ads`). */
    channel?: string
    /** Dashboard plan step to mark done when the composer message is submitted. */
    homeBriefingRecommendationId?: string
    /** Starting-mode id the created agent should begin in (e.g. `brand-status-quo`). */
    startingMode?: string
    /** Prompt-suggestion category to preselect in the composer (e.g. `research`, `static-ads`). */
    promptCategoryId?: string
    /** Submit starts the chat in the background (toast with an "Open chat" action) instead of
     *  navigating to it — the caller's browsing context survives the submit. */
    stayOnPage?: boolean
}) {
    const openOpts: ITaskComposerDispatchOptions = { openNewTask: true, autoSubmit }
    const { agentUuid } = resolveComposerAgentUuid(null, openOpts)
    if (channel) {
        pendingAgentAttachmentStore.getState().setPendingChannel(channel, agentUuid)
    }
    if (homeBriefingRecommendationId) {
        pendingAgentAttachmentStore
            .getState()
            .setPendingHomeBriefingRecommendationId(homeBriefingRecommendationId, agentUuid)
    }
    if (startingMode) {
        pendingAgentAttachmentStore.getState().setPendingStartingMode(startingMode, agentUuid)
    }
    if (promptCategoryId) {
        pendingAgentAttachmentStore.getState().setPendingPromptCategoryId(promptCategoryId, agentUuid)
    }
    if (stayOnPage) {
        pendingAgentAttachmentStore.getState().setPendingStayOnPage(agentUuid)
    }
    const allAttachments = [...(attachment ? [attachment] : []), ...(attachments ?? [])]
    for (const item of allAttachments) {
        dispatchAttachmentToActiveTaskTarget(item, null, openOpts)
    }
    if (prompt?.trim()) {
        dispatchPromptToActiveTaskTarget(prompt.trim(), null, openOpts)
    }
    if (skillMentions?.length) {
        pendingAgentAttachmentStore.getState().setPendingSkillMentions(skillMentions, agentUuid)
    }
    if (allAttachments.length === 0 && !prompt?.trim()) {
        scheduleGlobalTaskComposerOpen(autoSubmit)
    }
}
